import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNotifications } from '../../hooks/useNotifications';
import { useSelector } from 'react-redux';
import { NOTIFICATION_TYPES, parseNotificationMetadata, isNotificationExpired } from '../../types/notifications';
import notificationService from '../../services/notificationService';
import { toast } from 'react-toastify';

/**
 * NotificationTestPanel Component
 * Comprehensive testing interface for the notification system
 * Only visible to admin users for testing purposes
 */
const NotificationTestPanel = () => {
  
  // Use Redux directly instead of useAuth to avoid Router dependency
  const { user } = useSelector((state) => state.auth);
  const isAuthenticated = !!user; // Derive authentication status from user existence
  const { 
    notifications, 
    markAsRead, 
    deleteVideo,
    unreadCount,
    isConnected,
    error,
    isLoading,
    fetchNotifications
  } = useNotifications();

  const [testResults, setTestResults] = useState({});
  const [isRunningTests, setIsRunningTests] = useState(false);

  // Only show for admin users
  // Check if user has ADMIN role in the roles array
  const isAdmin = user?.roles?.includes('ADMIN');
  if (!isAuthenticated || !isAdmin) {
    return null;
  }

  /**
   * Test API endpoints
   */
  const testApiEndpoints = async () => {
    const results = { api: {}, websocket: {}, ui: {} };
    
    try {
      // Test 1: Fetch notifications API
      const fetchedNotifications = await notificationService.getUnreadNotifications();
      results.api.fetchNotifications = {

        count: fetchedNotifications.length
      };
    } catch (error) {
      results.api.fetchNotifications = {

        error: error.message
      };
    }

    // Test 2: Mark notification as read (if we have notifications)
    if (notifications.length > 0) {
      try {
        const firstNotification = notifications[0];
        await notificationService.markNotificationAsRead(firstNotification.id);
        results.api.markAsRead = {

          notificationId: firstNotification.id
        };
      } catch (error) {
        results.api.markAsRead = {

          error: error.message
        };
      }
    }

    return results;
  };

  /**
   * Test WebSocket connection
   */
  const testWebSocketConnection = () => {
    const results = {

      user: user?.username,
      endpoint: process.env.REACT_APP_WEBSOCKET_ENDPOINT || 'http://localhost:8080/ws'
    };
    
    return results;
  };

  /**
   * Test notification parsing and validation
   */
  const testNotificationParsing = () => {
    const results = {
      success: true, // Always successful if no parsing errors
      totalNotifications: notifications.length,
      expiredNotifications: 0,
      actionableNotifications: 0,
      overLimitNotifications: 0,
      parsingErrors: []
    };

    notifications.forEach(notification => {
      try {
        // Test expiration check
        if (isNotificationExpired(notification)) {
          results.expiredNotifications++;
        }

        // Test actionability check
        if (notification.actionLabel || notification.actionUrl) {
          results.actionableNotifications++;
        }

        // Test type filtering
        if (notification.type === NOTIFICATION_TYPES.OVER_LIMIT_PROMPT) {
          results.overLimitNotifications++;
        }

        // Test metadata parsing
        if (notification.metadataJson) {
          const metadata = parseNotificationMetadata(notification);
          if (metadata.suggestedVideoId) {
          }
        }
      } catch (error) {
        results.parsingErrors.push({
          notificationId: notification.id,
          error: error.message
        });
      }
    });

    return results;
  };

  /**
   * Run comprehensive tests
   */
  const runComprehensiveTests = async () => {
    setIsRunningTests(true);
    const results = {
      timestamp: new Date().toISOString(),
      user: user?.username,
      tests: {}
    };

    try {
      // Test API endpoints
      results.tests.api = await testApiEndpoints();
      
      // Test WebSocket connection
      results.tests.websocket = testWebSocketConnection();
      
      // Test notification parsing
      results.tests.parsing = testNotificationParsing();
      
      // Test UI components
      results.tests.ui = {
        success: !error, // Success if no error
        notificationsRendered: notifications.length,

        hasError: !!error
      };

      setTestResults(results);
      
      // Show summary toast
      const successCount = Object.values(results.tests).reduce((acc, test) => {
        return acc + Object.values(test).filter(result => result && result.success !== false).length;
      }, 0);
      
      toast.success(`Tests completed! ${successCount} checks passed`, {
        autoClose: 5000
      });

    } catch (error) {
      toast.error('Test execution failed: ' + error.message);
    } finally {
      setIsRunningTests(false);
    }
  };

  /**
   * Simulate over-limit notification (for testing)
   */
  const simulateOverLimitNotification = () => {
    const mockNotification = {
      id: Date.now(),
      type: NOTIFICATION_TYPES.OVER_LIMIT_PROMPT,
      title: "You're over your storage limit",
      body: "Delete your oldest video to free space and keep uploading.",
      actionLabel: "Delete oldest",
      actionUrl: "/library?suggestDelete=123",
      createdAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
      metadataJson: JSON.stringify({ suggestedVideoId: 123 })
    };

    // Add to notifications state (simulation)
    toast.warning('Simulated over-limit notification', {
      autoClose: 10000,

    });

  };

  /**
   * Test video deletion (mock)
   */
  const testVideoDeletion = async () => {
    try {
      const success = await deleteVideo(123); // Mock video ID
      toast.success('Video deletion test completed');
    } catch (error) {
      toast.error('Video deletion test failed: ' + error.message);
    }
  };

  return (
    <TestPanelContainer>
      <TestPanelHeader>
        <h2>🔔 Notification System Test Panel</h2>
        <p>Comprehensive testing interface for notification functionality</p>
      </TestPanelHeader>

      <StatusSection>
        <h3>System Status</h3>
        <StatusGrid>
          <StatusItem>
            <Label>WebSocket Connected:</Label>
            <Value status={isConnected ? 'success' : 'error'}>
              {isConnected ? '✅ Yes' : '❌ No'}
            </Value>
          </StatusItem>
          <StatusItem>
            <Label>Unread Notifications:</Label>
            <Value>{unreadCount}</Value>
          </StatusItem>
          <StatusItem>
            <Label>Loading State:</Label>
            <Value status={isLoading ? 'warning' : 'success'}>
              {isLoading ? '⏳ Loading' : '✅ Ready'}
            </Value>
          </StatusItem>
          <StatusItem>
            <Label>Error State:</Label>
            <Value status={error ? 'error' : 'success'}>
              {error ? `❌ ${error}` : '✅ No errors'}
            </Value>
          </StatusItem>
        </StatusGrid>
      </StatusSection>

      <TestSection>
        <h3>Test Actions</h3>
        <TestButtons>
          <TestButton 
            onClick={runComprehensiveTests}
            disabled={isRunningTests}
            variant="primary"
          >
            {isRunningTests ? '⏳ Running Tests...' : '🧪 Run Comprehensive Tests'}
          </TestButton>
          
          <TestButton 
            onClick={fetchNotifications}
            variant="secondary"
          >
            🔄 Refresh Notifications
          </TestButton>
          
          <TestButton 
            onClick={simulateOverLimitNotification}
            variant="warning"
          >
            📢 Simulate Over-Limit Notification
          </TestButton>
          
          <TestButton 
            onClick={testVideoDeletion}
            variant="info"
          >
            🗑️ Test Video Deletion
          </TestButton>
        </TestButtons>
      </TestSection>

      <NotificationsSection>
        <h3>Current Notifications ({notifications.length})</h3>
        {notifications.length === 0 ? (
          <EmptyState>No notifications found</EmptyState>
        ) : (
          <NotificationsList>
            {notifications.map(notification => (
              <NotificationItem key={notification.id}>
                <NotificationHeader>
                  <NotificationTitle>{notification.title}</NotificationTitle>
                  <NotificationType type={notification.type}>
                    {notification.type}
                  </NotificationType>
                </NotificationHeader>
                
                <NotificationBody>{notification.body}</NotificationBody>
                
                <NotificationMeta>
                  <MetaItem>
                    <strong>Created:</strong> {new Date(notification.createdAt).toLocaleString()}
                  </MetaItem>
                  {notification.expiresAt && (
                    <MetaItem>
                      <strong>Expires:</strong> {new Date(notification.expiresAt).toLocaleString()}
                      {isNotificationExpired(notification) && (
                        <span style={{ color: 'red', marginLeft: '8px' }}>⚠️ EXPIRED</span>
                      )}
                    </MetaItem>
                  )}
                  {notification.metadataJson && (
                    <MetaItem>
                      <strong>Metadata:</strong> {notification.metadataJson}
                    </MetaItem>
                  )}
                </NotificationMeta>
                
                <NotificationActions>
                  {notification.actionLabel && (
                    <ActionButton variant="primary">
                      {notification.actionLabel}
                    </ActionButton>
                  )}
                  <ActionButton 
                    variant="secondary"
                    onClick={() => markAsRead(notification.id)}
                  >
                    Mark as Read
                  </ActionButton>
                </NotificationActions>
              </NotificationItem>
            ))}
          </NotificationsList>
        )}
      </NotificationsSection>

      {Object.keys(testResults).length > 0 && (
        <TestResultsSection>
          <h3>Test Results</h3>
          <TestResults>
            <pre>{JSON.stringify(testResults, null, 2)}</pre>
          </TestResults>
        </TestResultsSection>
      )}
    </TestPanelContainer>
  );
};

// Styled Components
const TestPanelContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  margin: 2rem 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid #e1e5e9;
`;

const TestPanelHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  
  h2 {
    color: #2c3e50;
    margin: 0 0 0.5rem 0;
    font-size: 1.8rem;
  }
  
  p {
    color: #7f8c8d;
    margin: 0;
    font-size: 1rem;
  }
`;

const StatusSection = styled.div`
  margin-bottom: 2rem;
  
  h3 {
    color: #2c3e50;
    margin: 0 0 1rem 0;
    font-size: 1.3rem;
  }
`;

const StatusGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
`;

const StatusItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
`;

const Label = styled.span`
  font-weight: 500;
  color: #495057;
`;

const Value = styled.span`
  font-weight: 600;
  
  ${props => props.status === 'success' && `color: #28a745;`}
  ${props => props.status === 'error' && `color: #dc3545;`}
  ${props => props.status === 'warning' && `color: #ffc107;`}
`;

const TestSection = styled.div`
  margin-bottom: 2rem;
  
  h3 {
    color: #2c3e50;
    margin: 0 0 1rem 0;
    font-size: 1.3rem;
  }
`;

const TestButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`;

const TestButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.variant === 'primary' && `
    background: #007bff;
    color: white;
    
    &:hover:not(:disabled) {
      background: #0056b3;
    }
  `}
  
  ${props => props.variant === 'secondary' && `
    background: #6c757d;
    color: white;
    
    &:hover:not(:disabled) {
      background: #545b62;
    }
  `}
  
  ${props => props.variant === 'warning' && `
    background: #ffc107;
    color: #212529;
    
    &:hover:not(:disabled) {
      background: #e0a800;
    }
  `}
  
  ${props => props.variant === 'info' && `
    background: #17a2b8;
    color: white;
    
    &:hover:not(:disabled) {
      background: #138496;
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const NotificationsSection = styled.div`
  margin-bottom: 2rem;
  
  h3 {
    color: #2c3e50;
    margin: 0 0 1rem 0;
    font-size: 1.3rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: #6c757d;
  font-style: italic;
`;

const NotificationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const NotificationItem = styled.div`
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1rem;
  background: #f8f9fa;
`;

const NotificationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const NotificationTitle = styled.h4`
  margin: 0;
  color: #2c3e50;
  font-size: 1.1rem;
`;

const NotificationType = styled.span`
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 500;
  
  ${props => props.type === NOTIFICATION_TYPES.OVER_LIMIT_PROMPT && `
    background: #f8d7da;
    color: #721c24;
  `}
`;

const NotificationBody = styled.p`
  margin: 0 0 0.75rem 0;
  color: #495057;
  line-height: 1.4;
`;

const NotificationMeta = styled.div`
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
`;

const MetaItem = styled.div`
  margin-bottom: 0.25rem;
  color: #6c757d;
`;

const NotificationActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.variant === 'primary' && `
    background: #007bff;
    color: white;
    
    &:hover {
      background: #0056b3;
    }
  `}
  
  ${props => props.variant === 'secondary' && `
    background: #6c757d;
    color: white;
    
    &:hover {
      background: #545b62;
    }
  `}
`;

const TestResultsSection = styled.div`
  margin-top: 2rem;
  
  h3 {
    color: #2c3e50;
    margin: 0 0 1rem 0;
    font-size: 1.3rem;
  }
`;

const TestResults = styled.div`
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1rem;
  
  pre {
    margin: 0;
    font-size: 0.85rem;
    color: #495057;
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

export default NotificationTestPanel;
